import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaShoppingCart, FaPlus, FaMinus } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';
import { averageRatingFromReviews, clampRating0to5 } from '../../utils/ratingStars';
import namkeFallback from '../../assets/namke-fallback.png';

const DishDetailPopup = ({ 
  selectedDish, 
  closeDishPopup, 
  renderStars,
  formatPrice,
  formatDate,
  handleAddToCart
}) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [dishReviews, setDishReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  useEffect(() => {
    setQuantity(1);
  }, [selectedDish?.id]);

  useEffect(() => {
    if (!selectedDish?.id) {
      setDishReviews([]);
      return;
    }
    let cancelled = false;
    setReviewsLoading(true);
    fetch(`${API_BASE_URL}/client-reviews/public/dishes/${selectedDish.id}?limit=40`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setDishReviews(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setDishReviews([]);
      })
      .finally(() => {
        if (!cancelled) setReviewsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDish?.id]);

  const ratingFromReviews = useMemo(
    () => averageRatingFromReviews(dishReviews),
    [dishReviews],
  );

  const displayRating = useMemo(() => {
    if (!selectedDish) return 0;
    const api = selectedDish.rating != null ? clampRating0to5(selectedDish.rating) : null;
    if (ratingFromReviews != null && (api == null || api === 0)) return ratingFromReviews;
    if (api != null && api > 0) return api;
    return ratingFromReviews ?? 0;
  }, [selectedDish, ratingFromReviews]);

  const reviewCount = useMemo(() => {
    if (!selectedDish) return 0;
    return dishReviews.length > 0
      ? dishReviews.length
      : (selectedDish.review_count ?? selectedDish.reviews ?? 0);
  }, [selectedDish, dishReviews]);

  if (!selectedDish) return null;

  const handleAddToCartClick = () => {
    if (handleAddToCart) {
      handleAddToCart({
        ...selectedDish,
        quantity
      });
      closeDishPopup();
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
      <div style={{ background: '#FCFBF5', borderRadius: '28px', maxWidth: '640px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px', letterSpacing: '-0.02em' }}>{selectedDish.name}</h2>
          <button
            onClick={closeDishPopup}
            style={{ flexShrink: 0, border: 'none', cursor: 'pointer', width: '38px', height: '38px', borderRadius: '999px', background: 'transparent', color: 'rgba(5,16,4,0.4)', display: 'grid', placeItems: 'center' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(5,16,4,0.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <FaTimes size={16} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '26px', display: 'flex', flexDirection: 'column', gap: '22px' }}>
          {/* Image */}
          <div style={{ height: '240px', borderRadius: '22px', overflow: 'hidden' }}>
            <img
              src={selectedDish.dish_image_url || selectedDish.image_url || namkeFallback}
              alt={selectedDish.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Description */}
          <p style={{ margin: 0, fontSize: '15.5px', lineHeight: 1.65, color: 'rgba(5,16,4,0.8)' }}>
            {selectedDish.description || t('restaurant.noDescription')}
          </p>

          {/* Rating */}
          <div className="flex items-center flex-wrap gap-x-2" style={{ marginTop: '-8px' }}>
            {renderStars && renderStars(displayRating)}
            <span style={{ fontSize: '14px', color: 'rgba(5,16,4,0.66)' }}>
              {displayRating > 0 ? Number(displayRating).toFixed(1) : '—'}
              {' '}
              {reviewsLoading ? t('restaurant.loadingParens') : t('restaurant.reviewCountParens', { count: reviewCount })}
            </span>
          </div>

          {/* Avis clients sur ce plat — cartes compactes */}
          <div style={{ borderTop: '1px solid rgba(5,16,4,0.08)', paddingTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px' }}>
              {t('restaurant.reviewsOnDish')}
            </h3>
            {reviewsLoading && (
              <p style={{ margin: 0, fontSize: '13.5px', color: 'rgba(5,16,4,0.55)' }}>{t('restaurant.loadingReviews')}</p>
            )}
            {!reviewsLoading && dishReviews.length === 0 && (
              <p style={{ margin: 0, fontSize: '13.5px', color: 'rgba(5,16,4,0.55)' }}>{t('restaurant.noDishReviewsYet')}</p>
            )}
            {!reviewsLoading && dishReviews.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))', gap: '10px', maxHeight: '260px', overflowY: 'auto', paddingRight: '2px' }}>
                {dishReviews.map((r) => (
                  <div key={r.id} style={{ background: '#FBF9D9', borderRadius: '16px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div className="flex items-center justify-between gap-2">
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#054625' }}>
                        {r.user_name || t('restaurant.client')}
                      </span>
                      {renderStars && (
                        <span className="flex shrink-0">{renderStars(clampRating0to5(r.rating ?? 0))}</span>
                      )}
                    </div>
                    {r.comment && (
                      <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.5, color: 'rgba(5,16,4,0.75)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.comment}</p>
                    )}
                    {r.created_at && (
                      <p style={{ margin: 0, fontSize: '11.5px', color: 'rgba(5,16,4,0.45)' }}>
                        {formatDate
                          ? formatDate(r.created_at)
                          : new Date(r.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Price and Quantity */}
          <div style={{ borderTop: '1px solid rgba(5,16,4,0.08)', paddingTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em' }}>
              {formatPrice ? formatPrice(selectedDish.price) : `${selectedDish.price}€`}
            </span>

            <span style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#FBF9D9', borderRadius: '999px', padding: '5px' }}>
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                style={{ border: 'none', cursor: 'pointer', width: '34px', height: '34px', borderRadius: '999px', background: '#FCFBF5', color: '#054625', display: 'grid', placeItems: 'center' }}
              >
                <FaMinus size={11} />
              </button>
              <span style={{ minWidth: '26px', textAlign: 'center', fontWeight: 600, fontSize: '15px' }}>
                {quantity}
              </span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                style={{ border: 'none', cursor: 'pointer', width: '34px', height: '34px', borderRadius: '999px', background: '#054625', color: '#FBF9D9', display: 'grid', placeItems: 'center' }}
              >
                <FaPlus size={11} />
              </button>
            </span>
          </div>

          {/* Add to Cart Button */}
          <button
            onClick={handleAddToCartClick}
            style={{ border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16px', padding: '16px 24px', borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
          >
            <FaShoppingCart size={16} />
            <span>{t('restaurant.addToCartWithPrice', { price: formatPrice ? formatPrice(selectedDish.price * quantity) : `${(selectedDish.price * quantity).toFixed(2)}€` })}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DishDetailPopup;